import { DeepPartial } from "../../types";
import { IndividualShareholder, SignModeType, OTPType } from "../../contexts";
import { Invoice } from "../../views/LoanCalculator/service";

type WithId<T extends Record<string, unknown>> = {
  id: string;
} & T;

export type APIError = {
  message: string;
  error?: string;
};

export enum DocumentExportFormat {
  PDF = "pdf",
  XLSX = "xlsx",
}

export enum LoanProductType {
  FIXED_TERM = "FIXED_TERM",
  INVOICE_DISCOUNTING = "INVOICE_DISCOUNTING",
  LOC_INVOICE_DISCOUNTING = "LOC_INVOICE_DISCOUNTING",
  ACCOUNT_RECEIVABLES = "ACCOUNT_RECEIVABLES",
  PAYABLE_FINANCING = "PAYABLE_FINANCING",
  LOC_PAYABLE_FINANCING = "LOC_PAYABLE_FINANCING",
  LOC_RECEIVABLES_NON_RECOURSE = "LOC_RECEIVABLES_NON_RECOURSE",
  REVENUE_BASED_FINANCING = "REVENUE_BASED_FINANCING",
  POS_REVENUE_BASED_FINANCING = "POS_REVENUE_BASED_FINANCING",
  BNPL = "BNPL",
}

export enum LoanProductReason {
  PAY_SUPPLIER = "PAY_SUPPLIER",
  PAY_UNEXPECTED_BILL = "PAY_UNEXPECTED_BILL",
  COVER_CASH_FLOW_GAP = "COVER_CASH_FLOW_GAP",
  COVER_OPERATIONAL_EXPENSE = "COVER_OPERATIONAL_EXPENSE",
  BUY_NEW_EQUIPMENT = "BUY_NEW_EQUIPMENT",
  BUY_NEW_INVENTORY = "BUY_NEW_INVENTORY",
  GROW_BUSINESS = "GROW_BUSINESS",
  EXPAND_NEW_LOCATION = "EXPAND_NEW_LOCATION",
  NEED_WORKING_CAPITAL = "NEED_WORKING_CAPITAL",
  RECRUIT_STAFF = "RECRUIT_STAFF",
  UNPAID_INVOICES = "UNPAID_INVOICES",
}

export enum LoanDurationType {
  DAYS = "days",
  MONTHS = "months",
  WEEKS = "weeks",
  YEARS = "years",
}

export enum LoanApplicationStage {
  BRANDING = "BRANDING",
  CALCULATOR = "CALCULATOR",
  ONBOARDING = "ONBOARDING",
  BUSINESS_INFORMATION = "BUSINESS_INFORMATION",
  KYB = "KYB",
  COMPANY_INFORMATION = "COMPANY_INFORMATION",
  DOCUMENTS_UPLOAD = "DOCUMENTS_UPLOAD",
  OWNER_INFORMATION = "OWNER_INFORMATION",
  SHAREHOLDER_INFORMATION = "SHAREHOLDER_INFORMATION",
  KYC = "KYC",
  FINANCIAL_INFORMATION = "FINANCIAL_INFORMATION",
  BANK_STATEMENTS = "BANK_STATEMENTS",
  VAT_STATEMENTS = "VAT_STATEMENTS",
  GENERIC_DOCUMENTS = "GENERIC_DOCUMENTS",
  MORE_INFORMATION = "MORE_INFORMATION",
  MANUAL_ACCOUNTS = "MANUAL_ACCOUNTS",
  VENDOR_INFORMATION = "VENDOR_INFORMATION",
  REVIEW = "REVIEW",
  SUCCESS = "SUCCESS",
}

export enum LoanApplicationStatus {
  STARTED = "STARTED",
  SUBMITTED = "SUBMITTED",
  REJECTED = "REJECTED",
  CANCELLED = "CANCELLED",
  PREAPPROVED = "PREAPPROVED",
  ADDITIONAL_INFO_REQUESTED = "ADDITIONAL_INFO_REQUESTED",
  WITHDRAW = "WITHDRAW",
  ON_HOLD = "ON_HOLD",
  ACTIVE = "ACTIVE",
  PARTIALLY_DISBURSED = "PARTIALLY_DISBURSED",
  REVIEW_OFFER = "REVIEW_OFFER",
  COUNTER_OFFER_SENT = "COUNTER_OFFER_SENT",
  MISSING_INFORMATION = "MISSING_INFORMATION",
  APPROVED_WAITING_FOR_OFFER = "APPROVED_WAITING_FOR_OFFER",
  OFFER_ACCEPTED = "OFFER_ACCEPTED",
  OFFER_REJECTED = "OFFER_REJECTED",
  CONTRACT_PENDING_SIGNATURE = "CONTRACT_PENDING_SIGNATURE",
  CONTRACT_SIGNED = "CONTRACT_SIGNED",
  WAITING_FOR_DISBURSEMENT = "WAITING_FOR_DISBURSEMENT",
  REFER = "REFER",
  MLRO_REVIEW = "MLRO_REVIEW",
  UNDERWRITER_REJECTED = "UNDERWRITER_REJECTED",
  SYSTEM_REJECTED = "SYSTEM_REJECTED",
  INPRINCIPAL_APPROVAL = "INPRINCIPAL_APPROVAL",
  INREVIEW_MLRO = "INREVIEW_MLRO",
  DECLINED_MLRO = "DECLINED_MLRO",
  INREVIEW_OPS = "INREVIEW_OPS",
  INREVIEW_UW = "INREVIEW_UW",
  INREVIEW_SUW = "INREVIEW_SUW",
  INREVIEW_CRO = "INREVIEW_CRO",
  SME_REJECTED = "SME_REJECTED",
  DECLINED_SYSTEM = "DECLINED_SYSTEM",
  DECLINE_MLRO = "DECLINE_MLRO",
  DECLINED_UW = "DECLINED_UW",
  DECLINED_SUW = "DECLINED_SUW",
  APPROVED_SUW = "APPROVED_SUW",
  DECLINED_CRO = "DECLINED_CRO",
  APPROVED_CRO = "APPROVED_CRO",
  DECLINED_SME = "DECLINED_SME",
  CREDIT_MODIFICATION_LOAN_OPS = "CREDIT_MODIFICATION_LOAN_OPS",
  CREDIT_MODIFICATION_UW = "CREDIT_MODIFICATION_UW",
  WAITING_FOR_DP_REVIEW = "WAITING_FOR_DP_REVIEW",
  CLOSED = "CLOSED",
  FORECLOSED = "FORECLOSED",
  EARLY_CLOSURE = "EARLY_CLOSURE",
  FORCED_CLOSURE = "FORCED_CLOSURE",
  CLIENT_REVIEW_REJECTED = "CLIENT_REVIEW_REJECTED",
  EXPIRED = "EXPIRED",
}

export const LoanApplicationStatusDashboardMappingToText: Record<
  LoanApplicationStatus,
  string
> = {
  [LoanApplicationStatus.STARTED]: "Requested Facility",
  [LoanApplicationStatus.SUBMITTED]: "Requested Facility",
  [LoanApplicationStatus.REJECTED]: "Requested Facility",
  [LoanApplicationStatus.CANCELLED]: "Requested Facility",
  [LoanApplicationStatus.PREAPPROVED]: "Requested Facility",
  [LoanApplicationStatus.ADDITIONAL_INFO_REQUESTED]: "Requested Facility",
  [LoanApplicationStatus.WITHDRAW]: "Requested Facility",
  [LoanApplicationStatus.ON_HOLD]: "Requested Facility",
  [LoanApplicationStatus.ACTIVE]: "Disbursed Facility",
  [LoanApplicationStatus.PARTIALLY_DISBURSED]: "Disbursed Facility",
  [LoanApplicationStatus.REVIEW_OFFER]: "Approved Facility",
  [LoanApplicationStatus.COUNTER_OFFER_SENT]: "Approved Facility",
  [LoanApplicationStatus.MISSING_INFORMATION]: "Requested Facility",
  [LoanApplicationStatus.APPROVED_WAITING_FOR_OFFER]: "Approved Facility",
  [LoanApplicationStatus.OFFER_ACCEPTED]: "Approved Facility",
  [LoanApplicationStatus.OFFER_REJECTED]: "Proposed Facility",
  [LoanApplicationStatus.CONTRACT_PENDING_SIGNATURE]: "Approved Facility",
  [LoanApplicationStatus.CONTRACT_SIGNED]: "Approved Facility",
  [LoanApplicationStatus.WAITING_FOR_DISBURSEMENT]: "Approved Facility",
  [LoanApplicationStatus.REFER]: "Requested Facility",
  [LoanApplicationStatus.MLRO_REVIEW]: "Requested Facility",
  [LoanApplicationStatus.UNDERWRITER_REJECTED]: "Requested Facility",
  [LoanApplicationStatus.SYSTEM_REJECTED]: "Requested Facility",
  [LoanApplicationStatus.INPRINCIPAL_APPROVAL]: "Requested Facility",
  [LoanApplicationStatus.INREVIEW_MLRO]: "Requested Facility",
  [LoanApplicationStatus.DECLINED_MLRO]: "Requested Facility",
  [LoanApplicationStatus.INREVIEW_OPS]: "Requested Facility",
  [LoanApplicationStatus.INREVIEW_UW]: "Requested Facility",
  [LoanApplicationStatus.INREVIEW_SUW]: "Requested Facility",
  [LoanApplicationStatus.INREVIEW_CRO]: "Requested Facility",
  [LoanApplicationStatus.SME_REJECTED]: "Requested Facility",
  [LoanApplicationStatus.DECLINED_SYSTEM]: "Requested Facility",
  [LoanApplicationStatus.DECLINE_MLRO]: "Requested Facility",
  [LoanApplicationStatus.DECLINED_UW]: "Requested Facility",
  [LoanApplicationStatus.DECLINED_SUW]: "Requested Facility",
  [LoanApplicationStatus.APPROVED_SUW]: "Requested Facility",
  [LoanApplicationStatus.DECLINED_CRO]: "Requested Facility",
  [LoanApplicationStatus.APPROVED_CRO]: "Requested Facility",
  [LoanApplicationStatus.DECLINED_SME]: "Proposed Facility",
  [LoanApplicationStatus.CREDIT_MODIFICATION_LOAN_OPS]: "Requested Facility",
  [LoanApplicationStatus.CREDIT_MODIFICATION_UW]: "Requested Facility",
  [LoanApplicationStatus.WAITING_FOR_DP_REVIEW]: "Requested Facility",
  [LoanApplicationStatus.CLOSED]: "Disbursed Facility",
  [LoanApplicationStatus.EARLY_CLOSURE]: "Disbursed Facility",
  [LoanApplicationStatus.FORCED_CLOSURE]: "Disbursed Facility",
  [LoanApplicationStatus.FORECLOSED]: "Disbursed Facility",
  [LoanApplicationStatus.EXPIRED]: "Requested Facility",
  [LoanApplicationStatus.CLIENT_REVIEW_REJECTED]: "Requested Facility",
};

export enum MediaType {
  IMAGE = "IMAGE",
  VIDEO = "VIDEO",
  DOCUMENT = "DOCUMENT",
}

export enum MediaPurpose {
  TRADE_LICENSE = "TRADE_LICENSE",
  MEMORANDUM_OF_ASSOCIATION = "MEMORANDUM_OF_ASSOCIATION",
  POWER_OF_ATTORNEY = "POWER_OF_ATTORNEY",
  VAT_STATEMENT = "VAT_STATEMENT",
  EMIRATES_ID_FRONT = "EMIRATES_ID_FRONT",
  EMIRATES_ID_BACK = "EMIRATES_ID_BACK",
  PASSPORT = "PASSPORT",
  FACE_ID = "FACE_ID",
  BANK_STATEMENT = "BANK_STATEMENT",
  BANK_ACCOUNT_CONNECTION = "BANK_ACCOUNT_CONNECTION",
  SIGNATURE = "SIGNATURE",
  SIGNATURE_STROKE = "SIGNATURE_STROKE",
  DIRECT_DEBIT_SIGNED_FORM = "DIRECT_DEBIT_SIGNED_FORM",
  INVOICE = "INVOICE",
  SALES_STATEMENT = "SALES_STATEMENT",
  POS_SALES_STATEMENT = "POS_SALES_STATEMENT",
  GENERIC_DOCUMENT = "GENERIC_DOCUMENT",
  SALES_INVOICE = "SALES_INVOICE",
  DRAWDOWN_INVOICE = "DRAWDOWN_INVOICE",
  VENDOR_TRADE_LICENSE = "VENDOR_TRADE_LICENSE",
  VENDOR_INVOICE = "VENDOR_INVOICE",
  VENDOR_VAT_STATEMENT = "VENDOR_VAT_STATEMENT",
  VENDOR_IBAN_LETTER = "VENDOR_IBAN_LETTER",
  VENDOR_ADDITIONAL_DOCUMENT = "VENDOR_ADDITIONAL_DOCUMENT",
  IBAN_LETTER = "IBAN_LETTER",
  VAT_CERTIFICATE = "VAT_CERTIFICATE",
}

export enum OpenBankingStatus {
  INITIATED = "INITIATED",
  IN_PROGRESS = "IN_PROGRESS",
  SUCCESSFUL = "SUCCESSFUL",
  FAILED = "FAILED",
  CANCELLED = "CANCELLED",
  RECONNECT_REQUIRED = "RECONNECT_REQUIRED",
  RESULTS_PENDING = "RESULTS_PENDING",
}

export enum ShareholderEntityType {
  INDIVIDUAL = "INDIVIDUAL",
  COMPANY = "COMPANY",
}

export enum CompanyShareholderType {
  APPLICANT = "APPLICANT",
  SHAREHOLDER = "SHAREHOLDER",
}

export enum ShareholderKYCApplicationStatus {
  PENDING = "PENDING",
  INITIATED = "INITIATED",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
}

export enum LoanOfferStage {
  REVIEW = "REVIEW",
  SETUP_DIRECT_DEBIT = "SETUP_DIRECT_DEBIT",
  SELECT_BANK_ACCOUNT = "SELECT_BANK_ACCOUNT",
  ADD_BANK_ACCOUNT = "ADD_BANK_ACCOUNT",
  SELECT_SIGN_MODE = "SELECT_SIGN_MODE",
  COMPLETED = "COMPLETED",
  DECLINED = "DECLINED",
}

export enum DirectDebitBankAccountSourceType {
  LEANTECH_CONNECTED = "LEANTECH_CONNECTED",
  PDF_BANK_STATEMENT = "PDF_BANK_STATEMENT",
  MANUAL = "MANUAL",
}

export enum OpenbankingApplicationStage {
  INITIATED = "INITIATED",
  WAITING_FOR_WEBHOOK = "WAITING_FOR_WEBHOOK",
  FETCH_ACCOUNTS = "FETCH_ACCOUNTS",
  FETCH_TRANSACTIONS = "FETCH_TRANSACTIONS",
  FETCH_BALANCES = "FETCH_BALANCES",
  COMPLETED = "COMPLETED",
  CANCELLED = "CANCELLED",
}

export enum AdditionalInfoType {
  SHAREHOLDERS_CONTACTS = "SHAREHOLDERS_CONTACTS", //info about shareholders
  DOC_TLN = "DOC_TLN", // Trade License Document
  DOC_MOA = "DOC_MOA", // Memorandum of Association Document
  DOC_POA = "DOC_POA", // Power of Attorney Document
  DOC_VAT = "DOC_VAT", // VAT Document
  DOC_BANK = "DOC_BANK", // Bank Statements Document
  DOC_OTHER = "DOC_OTHER", // Any Other Document
  DOC_SALES_STATEMENT = "DOC_SALES_STATEMENT", // Sales Statement Document
  DOC_POS_SALES_STATEMENT = "DOC_POS_SALES_STATEMENT", // POS Sales Statement Document
  SHAREHOLDERS_AECB = "SHAREHOLDERS_AECB", // Shareholders AECB
  DOC_SALES_INVOICE = "DOC_SALES_INVOICE", // Sales Invoice Document
  DOC_IBAN_LETTER = "DOC_IBAN_LETTER",
  DOC_VAT_CERTIFICATE = "DOC_VAT_CERTIFICATE",
}

export type AdditionalInfo = {
  loanApplicationId: string;
  correlationId: string;
  type: AdditionalInfoType;
  hint?: string;
  userId: number;
  shareholder?: IndividualShareholder;
};

export type CreateLoanApplicationRequest = {
  user: Partial<{
    name: string;
    email?: string;
    countryCode: string;
    localNumber: string;
    phoneNumber: string;
    otpCode: string;
    otpType: OTPType;
  }>;
  loanType?: LoanProductType;
  reason?: string;
  loanApplicationId?: string;
  refId?: string;
};

export type Company = {
  id: string;
  name: string;
  businessEmail: string;
  licenseNumber: string;
  annualRevenue: number;
  taxRegistrationNumber: string;
  estabilishedYear: string;
  type: string;
  officeEmirate: string;
  officeArea: string;
  officeStreet: string;
  officeBuilding: string;
  officeFloor: string;
  documents: {
    tradeLicense: string;
    memorandumOfAssociation: string;
    powerOfAttorney: string;
    q1VatStatement: string;
    q2VatStatement: string;
    q3VatStatement: string;
    q4VatStatement: string;
  };
  individualShareholders?: {
    firstName: string;
    lastName: string;
    hasPOA?: boolean;
  }[];
};

export type UpdateLoanApplicationRequest = { id: string } & DeepPartial<{
  stage: LoanApplicationStage;
  refId?: string;
  company: {
    id: string;
    name: string;
    businessEmail: string;
    licenseNumber: string;
    annualRevenue: number;
    taxRegistrationNumber: string;
    estabilishedYear: string;
    type: string;
    officeEmirate: string;
    officeArea: string;
    officeStreet: string;
    officeBuilding: string;
    officeFloor: string;
    documents: {
      tradeLicense: string;
      memorandumOfAssociation: string;
      powerOfAttorney: string;
      q1VatStatement: string;
      q2VatStatement: string;
      q3VatStatement: string;
      q4VatStatement: string;
    };
  };
  loanInput: {
    type: LoanProductType;
    amount: number;
    duration: number;
  };
  user: {
    name: string;
    email: string;
    phoneNumber: string;
  };
  additionalDPMetadata: Record<string, string> | null;
}>;

export type UploadMediaRequest = {
  name: string;
  type: MediaType;
  purpose: MediaPurpose;
  contentType: string;
  size: number;
  loanApplicationId: string;
};

export type FinalizeMediaRequest = {
  mediaId: string;
};

export type UploadMedia = {
  url: string;
  file: File;
};

export type InitiateOpenBankingRequest = {
  loanApplicationId: string;
};

export type UpdateOpenBankingStageRequest = {
  loanApplicationId: string;
  stage: OpenbankingApplicationStage;
};

export type GetOpenBankingStatusRequest = {
  loanApplicationId: string;
};

export type SendOTPRequest = {
  countryCode?: string;
  localNumber?: string;
};

export type SendOTPByLoanApplicationIdRequest = {
  loanApplicationId: string;
};

export type SendEmailOTPRequest = {
  email: string;
};

export type VerifyOTPRequest = {
  otpCode: string;
};

export type DeclineLoanOfferRequest = {
  id: string;
  reason: string;
};

export type ApproveLoanOfferRequest = {
  id: string;
  shareholderSignMode?: SignModeType;
};

export type UpdateLoanOfferStageRequest = {
  id: string;
  stage: LoanOfferStage;
};

export enum AecbConsentStatus {
  PENDING = "PENDING",
  RECEIVED = "RECEIVED",
  DECLINED = "DECLINED",
}

export type CompanyShareholderAecbConsentContext = {
  consentRecordId: string;
  status: AecbConsentStatus;
  isExpired: boolean;
  canDecide: boolean;
  expiresAt: string;
  signatoryName: string;
  corporateEntityName: string;
  facilityFor: string;
  facilityType: string;
  facilityAmount: string | null;
  roleLabel: string;
  assessmentPurpose: string;
  monitoringPurpose: string;
  dp: {
    id: string;
    name: string;
    themeConfig?: Record<string, unknown>;
    uiLocalizationData?: Record<string, unknown>;
  };
};

export type SaveDirectDebitBankAccountRequest = {
  loanOfferId: string;
  accountId: string;
  accountName: string;
  bankName: string;
  iban: string;
  sourceType: DirectDebitBankAccountSourceType;
};

export type GetSignatureStrokeRequest = {
  mediaId: string;
  shareholderId: string;
};

export type GetExistingShareholderSignatureSignelUrlRequest = {
  loanApplicationId: string;
  shareholderId: string;
};

export type GetMediaResponse = {
  media: {
    id: string;
    path: string;
    purpose: MediaPurpose;
    type: MediaType;
    thumbnailPath: string;
    fileName: string;
    metdata: {
      userId?: string;
      loanApplicationId?: string;
    };
  };
  url: string;
  thumbnailUrl: string;
};

export type VerifyOriginRequest = {
  origin: string;
};

export type VerifyOriginResponse = {
  allowed: boolean;
};

export type GetUserResponse = {
  countryCode: string;
  email: string;
  localNumber: string;
  name: string;
  id: string;
  distributionPartner: {
    id: string;
    name: string;
  };
};

export type UpdateIndividualShareholderRequest = WithId<
  Partial<{
    firstName: string;
    lastName: string;
    email: string;
    countryCode: string;
    localNumber: string;
    isStakeholder: boolean;
    hasPOA: boolean;
  }>
>;

export type UpdateCompanyShareholderRequest = WithId<
  Partial<{
    name: string;
    email: string;
    countryCode: string;
    localNumber: string;
  }>
>;

export type InitiateCopyRequest = {
  loanApplicationId: string; // loanAppId TO which we need to copy
  refId: string; // loanAppId FROM which we ween to copy data
};

export type ValidateLoanApplicationUniquenessRequest = {
  tradeLicenseNumber: string;
  licensingAuthority: string;
  loanId: string; // already created loan application Id
};

export type SaveMastercardMerchantIdsRequest = {
  merchantIds: string[];
  loanApplicationId: string;
};

export type DeleteMastercardMerchantIdsRequest = {
  merchantIds: string[];
  loanApplicationId: string;
};

export type UpdateMastercardMerchantIdsRequest = {
  merchantId: string;
  loanApplicationId: string;
  merchantDataId: string;
};

export type UpdateAdditionalInfo = WithId<{
  infoType: AdditionalInfoType;
  userId?: number;
  values?: string[];
}>;

export enum PaymentTransactionsSortColumns {
  DATE = "DATE",
  REMITTER = "REMITTER",
  TRANSACTION_ID = "TRANSACTION_ID",
  AMOUNT = "AMOUNT",
}

export enum SortOrder {
  ASC = "ASC",
  DESC = "DESC",
}

export interface UsePaymentDetailsProps {
  loanApplicationId: string;
  paymentIds: string[];
  search?: string;
  sortColumn?: PaymentTransactionsSortColumns;
  sortOrder?: SortOrder;
  limit?: number;
}

type StatusLabels<T extends {}> = {
  [k in keyof T]: {
    label: string;
    value: string;
    displayClasses: string;
  };
};
export const LoanApplicationStatusLabels: StatusLabels<
  typeof LoanApplicationStatus
> = {
  STARTED: {
    label: "Started",
    value: LoanApplicationStatus.STARTED,
    displayClasses: "cx-text-text-secondary cx-bg-background-secondary",
  },
  SUBMITTED: {
    label: "Submitted",
    value: LoanApplicationStatus.SUBMITTED,
    displayClasses: "cx-text-[#8E6A00] cx-bg-[#FCF4D6]",
  },
  WAITING_FOR_DP_REVIEW: {
    label: "In review",
    value: LoanApplicationStatus.WAITING_FOR_DP_REVIEW,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  REJECTED: {
    label: "Rejected",
    value: LoanApplicationStatus.REJECTED,
    displayClasses: "cx-bg-red-100 cx-text-red-700",
  },
  CANCELLED: {
    label: "Cancelled",
    value: LoanApplicationStatus.CANCELLED,
    displayClasses: "cx-bg-red-100 cx-text-red-700",
  },
  PREAPPROVED: {
    label: "In review",
    value: LoanApplicationStatus.PREAPPROVED,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  ADDITIONAL_INFO_REQUESTED: {
    label: "Action required",
    value: LoanApplicationStatus.ADDITIONAL_INFO_REQUESTED,
    displayClasses: "cx-bg-red-100 cx-text-red-600",
  },
  WITHDRAW: {
    label: "Withdrawn",
    value: LoanApplicationStatus.WITHDRAW,
    displayClasses: "cx-text-[#8E6A00] cx-bg-[#FCF4D6]",
  },
  ON_HOLD: {
    label: "On hold",
    value: LoanApplicationStatus.ON_HOLD,
    displayClasses: "cx-text-[#EB6200] cx-bg-[#FEF6EE]",
  },
  ACTIVE: {
    label: "Active",
    value: LoanApplicationStatus.ACTIVE,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  PARTIALLY_DISBURSED: {
    label: "Partially disbursed",
    value: LoanApplicationStatus.PARTIALLY_DISBURSED,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  REVIEW_OFFER: {
    label: "Offer sent",
    value: LoanApplicationStatus.REVIEW_OFFER,
    displayClasses: "cx-text-[#0064FF] cx-bg-[#EDF5FF]",
  },
  MISSING_INFORMATION: {
    label: "Action required",
    value: LoanApplicationStatus.MISSING_INFORMATION,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  APPROVED_WAITING_FOR_OFFER: {
    label: "In review",
    value: LoanApplicationStatus.APPROVED_WAITING_FOR_OFFER,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  OFFER_ACCEPTED: {
    label: "Offer accepted",
    value: LoanApplicationStatus.OFFER_ACCEPTED,
    displayClasses: "cx-text-[#108723] cx-bg-[#E8FFED]",
  },
  OFFER_REJECTED: {
    label: "Offer rejected",
    value: LoanApplicationStatus.OFFER_REJECTED,
    displayClasses: "cx-bg-red-100 cx-text-red-700",
  },
  COUNTER_OFFER_SENT: {
    label: "Offer sent",
    value: LoanApplicationStatus.COUNTER_OFFER_SENT,
    displayClasses: "cx-text-[#0064FF] cx-bg-[#EDF5FF]",
  },
  CONTRACT_PENDING_SIGNATURE: {
    label: "Contract pending signature",
    value: LoanApplicationStatus.CONTRACT_PENDING_SIGNATURE,
    displayClasses: "cx-text-[#EB6200] cx-bg-[#FEF6EE]",
  },
  CONTRACT_SIGNED: {
    label: "Contract signed",
    value: LoanApplicationStatus.CONTRACT_SIGNED,
    displayClasses: "cx-text-[#108723] cx-bg-[#E8FFED]",
  },
  WAITING_FOR_DISBURSEMENT: {
    label: "Awaiting Disbursement",
    value: LoanApplicationStatus.WAITING_FOR_DISBURSEMENT,
    displayClasses: "cx-text-brand-primary-regular cx-bg-brand-primary-lighter",
  },
  REFER: {
    label: "In review",
    value: LoanApplicationStatus.REFER,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  MLRO_REVIEW: {
    label: "In review",
    value: LoanApplicationStatus.MLRO_REVIEW,
    displayClasses: "cx-text-success-dark cx-bg-success-lighter",
  },
  UNDERWRITER_REJECTED: {
    label: "Rejected",
    value: LoanApplicationStatus.UNDERWRITER_REJECTED,
    displayClasses: "cx-bg-red-100 cx-text-red-700",
  },
  CLIENT_REVIEW_REJECTED: {
    label: "Rejected",
    value: LoanApplicationStatus.CLIENT_REVIEW_REJECTED,
    displayClasses: "cx-bg-red-100 cx-text-red-700",
  },
  SYSTEM_REJECTED: {
    label: "Rejected",
    value: LoanApplicationStatus.SYSTEM_REJECTED,
    displayClasses: "cx-bg-red-100 cx-text-red-700",
  },
  INPRINCIPAL_APPROVAL: {
    label: "In review",
    value: LoanApplicationStatus.INPRINCIPAL_APPROVAL,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  INREVIEW_MLRO: {
    label: "In review",
    value: LoanApplicationStatus.INREVIEW_MLRO,
    displayClasses: "cx-text-success-dark cx-bg-success-lighter",
  },
  DECLINED_MLRO: {
    label: "Rejected",
    value: LoanApplicationStatus.DECLINED_MLRO,
    displayClasses: "cx-bg-red-100 cx-text-red-700",
  },
  INREVIEW_OPS: {
    label: "In review",
    value: LoanApplicationStatus.INREVIEW_OPS,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  INREVIEW_UW: {
    label: "In review",
    value: LoanApplicationStatus.INREVIEW_UW,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  INREVIEW_SUW: {
    label: "In review",
    value: LoanApplicationStatus.INREVIEW_SUW,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  INREVIEW_CRO: {
    label: "In review",
    value: LoanApplicationStatus.INREVIEW_CRO,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  SME_REJECTED: {
    label: "Rejected",
    value: LoanApplicationStatus.SME_REJECTED,
    displayClasses: "cx-bg-red-100 cx-text-red-700",
  },
  DECLINED_SYSTEM: {
    label: "Rejected",
    value: LoanApplicationStatus.DECLINED_SYSTEM,
    displayClasses: "cx-bg-red-100 cx-text-red-700",
  },
  DECLINE_MLRO: {
    label: "Rejected",
    value: LoanApplicationStatus.DECLINE_MLRO,
    displayClasses: "cx-bg-red-100 cx-text-red-700",
  },
  DECLINED_UW: {
    label: "In review",
    value: LoanApplicationStatus.DECLINED_UW,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  DECLINED_SUW: {
    label: "Rejected",
    value: LoanApplicationStatus.DECLINED_SUW,
    displayClasses: "cx-bg-red-100 cx-text-red-700",
  },
  APPROVED_SUW: {
    label: "In review",
    value: LoanApplicationStatus.APPROVED_SUW,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  DECLINED_CRO: {
    label: "Rejected",
    value: LoanApplicationStatus.DECLINED_CRO,
    displayClasses: "cx-bg-red-100 cx-text-red-700",
  },
  APPROVED_CRO: {
    label: "In review",
    value: LoanApplicationStatus.APPROVED_CRO,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  DECLINED_SME: {
    label: "Offer rejected",
    value: LoanApplicationStatus.DECLINED_SME,
    displayClasses: "cx-bg-red-100 cx-text-red-700",
  },
  CREDIT_MODIFICATION_LOAN_OPS: {
    label: "In review",
    value: LoanApplicationStatus.CREDIT_MODIFICATION_LOAN_OPS,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  CREDIT_MODIFICATION_UW: {
    label: "In review",
    value: LoanApplicationStatus.CREDIT_MODIFICATION_UW,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  CLOSED: {
    label: "Closed",
    value: LoanApplicationStatus.CLOSED,
    displayClasses: "cx-text-[#666666] cx-bg-[#EEEEEE]",
  },
  FORECLOSED: {
    label: "Closed",
    value: LoanApplicationStatus.FORECLOSED,
    displayClasses: "cx-text-[#666666] cx-bg-[#EEEEEE]",
  },
  EARLY_CLOSURE: {
    label: "Closed",
    value: LoanApplicationStatus.EARLY_CLOSURE,
    displayClasses: "cx-text-[#666666] cx-bg-[#EEEEEE]",
  },
  FORCED_CLOSURE: {
    label: "Closed",
    value: LoanApplicationStatus.FORCED_CLOSURE,
    displayClasses: "cx-text-[#666666] cx-bg-[#EEEEEE]",
  },
  EXPIRED: {
    label: "Expired",
    value: LoanApplicationStatus.EXPIRED,
    displayClasses: "cx-bg-red-100 cx-text-red-700",
  },
};

export const LOAN_PRODUCT_DISPLAY_NAME: Record<LoanProductType, string> = {
  FIXED_TERM: "Advance Cashflow",
  INVOICE_DISCOUNTING: "Receivable Finance",
  LOC_INVOICE_DISCOUNTING: "Receivable Facility",
  ACCOUNT_RECEIVABLES: "Account Receivables",
  PAYABLE_FINANCING: "Payable Finance",
  LOC_PAYABLE_FINANCING: "Payable Facility",
  LOC_RECEIVABLES_NON_RECOURSE: "Receivables Non-Recourse Facility",
  REVENUE_BASED_FINANCING: "Revenue Based Finance",
  BNPL: "Buy Now Pay Later",
  POS_REVENUE_BASED_FINANCING: "POS Revenue Based Finance",
} as const;

export type MonthlyAmounts = {
  paidAmount: number;
  exceededAmount: number;
  outstandingAmount: number;
  installmentAmount: number;
  loanContractScheduleIds: string[];
  installmentDates: string[];
  tag: string;
};

export type PaymentHistoryDTO = {
  [key: string]: MonthlyAmounts;
};

export type PaymentHistoryResponseDTO = {
  history: PaymentHistoryDTO;
};

export enum LoanContractLoanFrequency {
  DAYS = "DAYS",
  MONTHLY = "MONTHLY",
  BIWEEKLY = "BIWEEKLY",
}

export type PaymentSummaryResponseDTO = {
  monthDueAmount: number;
  monthInstallmentAmount: number;
  totalOutstandingAmount: number;
  totalInstallmentAmount: number;
  tenure?: number;
  loanFrequency?: LoanContractLoanFrequency;
};

export type LoanApplicationsSummaryCompanyProfile = {
  companyId: string;
  masterCompanyId: string;
  name: string;
  licenseNumber: string;
  licensingAuthority: string;
  loanApplicationId: string;
};

export type LoanApplicationFlags = {
  allowNewLoanApplication?: boolean;
  continueExistingLoanApplication?: boolean;
  isFirstLoanApplication?: boolean;
};

export type LoanApplicationFlagsAndProfilesResponse = {
  uniqueCompanyProfiles?: LoanApplicationsSummaryCompanyProfile[];
  flags?: LoanApplicationFlags;
};

export type LoanApplicationsSummaryResponse = {
  totalAmountDisbursed: number;
  totalOutstandingAmount: number;
  overdueInstallmentAmount: number;
  overdueInstallmentPrincipal: number;
  overdueInstallmentInterest: number;
  overdueInstallmentLateFee: number;
  overdueDaysCount: number;
  upcomingInstallmentsCount: number;
  upcomingInstallmentsAmount: number;
  upcomingInvoicesCount: number;
  upcomingPaymentDate: string | null;
  creditLineFacilitiesCount: number;
  termLoanFacilitiesCount: number;
  totalApprovedFacilityAmount: number;
  creditLineTotalApprovedAmount: number;
  creditLineTotalUtilisedAmount: number;
  creditLineTotalOutstandingAmount: number;
  termLoanTotalApprovedAmount: number;
  termLoanTotalOutstandingAmount: number;
};

export type LoanDashboardResponse = {
  loanApplications: any[];
  summary: LoanApplicationsSummaryResponse | null;
};

export type PaymentDetailsResponseDTO = {
  transactionId: string;
  remitter: string;
  amount: number;
  date: string;
};

export type LoanContractScheduleTransactionListDTO = {
  transactions: Array<PaymentDetailsResponseDTO>;
  count: number;
  lastUpdatedAt: string;
};

export type LatestTransactionResponseDTO = {
  date: string;
};

export type ManualAccount = {
  id: string;

  companyId: string;

  name?: string;

  currency?: string;

  bankName?: string;
  iban?: string;
};
export type ManualAccountsResponseDTO = {
  accounts: ManualAccount[];
};

export type LoanApplicationUniquenessResponseDTO = {
  isDuplicateApplication: boolean;
  isPhoneNumberMatching: boolean;
  sourceId: string; // loanId of application found with same trade license and licensing authority
  phoneNumber: string;
};

export type LogoDevBrand = {
  name: string;
  // logo_url?: string;
  // domain: string;
};

export type GetVendorsRequest = {
  loanApplicationId: string;
  order?: SortOrder;
  page?: number;
  take?: number;
  search?: string;
  disablePagination?: boolean;
  startDate?: string;
  endDate?: string;
  sources?: string[];
  pageSize?: number;
  status?: LoanVendorStatus;
  type?: LoanVendorType;
};

export type ListVendorsResponse = {
  vendors: VendorListItem[];
  meta: {
    page: number;
    pageSize: number;
    itemsCount: number;
    pageCount: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
  };
};

export type VendorListItem = {
  vendorId: string;
  name: string;
  country: string;
  type: LoanVendorType;
  status: LoanVendorStatus;
};

export enum LoanVendorType {
  SUPPLIER = "SUPPLIER",
  BUYER = "BUYER",
}

export enum LoanVendorStatus {
  // DRAFT is when the LoanVendor has been created but not yet submitted for review.
  DRAFT = "DRAFT",

  // PENDING_REVIEW is when the LoanVendor has been verified and now is waiting
  // a review by Mission-Control.
  PENDING_REVIEW = "PENDING_REVIEW",

  // PENDING_DOCUMENTS is when the LoanVendor is missing some documents, per review
  // from Mission-Control, and it might be approved if more supporting documents
  // are provided.
  PENDING_DOCUMENTS = "PENDING_DOCUMENTS",

  // ON_HOLD is when Mission-Control decided that they want to put this vendor
  // on hold for future
  ON_HOLD = "ON_HOLD",
  VERIFIED_BY_OPS = "VERIFIED_BY_OPS",
  APPROVED_BY_UW = "APPROVED_BY_UW",
  REJECTED_BY_OPS = "REJECTED_BY_OPS",
  REJECTED_BY_UW = "REJECTED_BY_UW",
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  EXPIRED = "EXPIRED",
  CLOSED = "CLOSED",
}

export enum LoanVendorDocumentRequestStatus {
  DRAFT = "DRAFT",
  PENDING = "PENDING",
  COMPLETED = "COMPLETED",
}

export type CreateVendorRequest = {
  loanApplicationId: string;
  name: string;
  country: string;
  licenseNumber?: string;
  registrationAuthority?: string;
  type: LoanVendorType;
  tradeLicenseDocumentIds?: string[];
  invoicesDocumentIds?: string[];
  vatCertificateDocumentIds?: string[];
  ibanDocumentIds?: string[];
  additionalDocumentIds?: string[];
};

export type GetApplicationVendorsRequest = {
  loanApplicationId: string;
  search?: string;
  page?: number;
  take?: number;
  order?: SortOrder;
  disablePagination?: boolean;
  status?: LoanVendorStatus;
  type?: LoanVendorType;
};

export interface LoanVendorDocumentDTO {
  documentId: string;
  documentPurpose: MediaPurpose;
  documentUrl: string;
  status: LoanVendorDocumentStatus;
  //incase we get details from loan application details endpoint
  document?: {
    id?: string;
    purpose?: MediaPurpose;
  };
}

export enum LoanVendorManagementAction {
  SUBMIT_DOCUMENTS = "SUBMIT_DOCUMENTS",
  CONTINUE_WITHOUT_PENDING_VENDORS = "CONTINUE_WITHOUT_PENDING_VENDORS",
}

export enum LoanVendorDocumentStatus {
  DRAFT = "DRAFT",
  ACTIVE = "ACTIVE",
}

export enum VendorMediaPurpose {
  VENDOR_TRADE_LICENSE = "VENDOR_TRADE_LICENSE",
  VENDOR_INVOICE = "VENDOR_INVOICE",
  VENDOR_VAT_STATEMENT = "VENDOR_VAT_STATEMENT",
  VENDOR_IBAN_LETTER = "VENDOR_IBAN_LETTER",
  VENDOR_ADDITIONAL_DOCUMENT = "VENDOR_ADDITIONAL_DOCUMENT",
  BANK_STATEMENT = "BANK_STATEMENT",
}

export type VendorMediaType = Extract<
  keyof typeof MediaPurpose,
  keyof typeof VendorMediaPurpose
>;

export enum LoanVendorDocumentRequestReason {
  MISSING_DOCUMENT = "MISSING_DOCUMENT",
  EXPIRED_DOCUMENT = "EXPIRED_DOCUMENT",
  OTHER_REASON = "OTHER_REASON",
}

export interface VendorDocumentRequestDTO {
  id: string;
  type: VendorMediaType;
  loanVendorId: LoanVendorDocumentRequestReason;
  reason: string;
  reasonDescription?: string;
  status: LoanVendorDocumentRequestStatus;
  loanVendorDocuments: LoanVendorDocumentDTO[];
}

export interface Vendor {
  id?: string;
  loanVendorId: string;
  vendorId: string;
  name: string;
  country: string;
  status?: LoanVendorStatus;
  licenseNumber?: string;
  registrationAuthority?: string;
  type: LoanVendorType;
  tradeLicenseDocumentIds?: string[];
  invoicesDocumentIds?: string[];
  vatCertificateDocumentIds?: string[];
  ibanCertificatesDocumentIds?: string[];
  additionalDocumentIds?: string[];
  documents?: LoanVendorDocumentDTO[];
  loanVendorDocumentRequests?: VendorDocumentRequestDTO[];
  subLimitAmount?: number | null;
  subLimitPercentage?: number | null;
  /** LMS vendor utilization (Active LOC) */
  outstandingAmount?: number | null;
  /** subLimitAmount − outstanding (Active LOC) */
  headroom?: number | null;
  vendorContractExpiryDate?: string | null;
}

export interface UpdateLoanVendorDocumentRequestDTO {
  documentIds: string[];
  status?: LoanVendorDocumentRequestStatus;
}

export interface UpdateVendorRequest extends CreateVendorRequest {
  loanVendorId: string;
}

export type PaginatedResponse<T> = {
  data: T[];
  meta: {
    page: number;
    pageSize: number;
    skip: number;
    itemsCount: number;
    pageCount: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
};

export type PageMeta = {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  itemsCount: number;
  page: number;
  pageCount: number;
  pageSize: number;
  skip: number;
};

export type TransactionDTO = {
  transactionId: string;
  remitter?: string;
  paidAmount: number;
  timestamp: string;
};

export type TransactionsResponseDTO = {
  data: TransactionDTO[];
  meta: PageMeta;
};

export interface GetAllTransactionsProps {
  page?: number;
  pageSize?: number;
  disablePagination?: boolean;
  loanApplicationId: string;
  filters?: {
    searchText?: string;
    sortColumn?: PaymentTransactionsSortColumns;
    sortOrder?: SortOrder;
    limit?: number;
  };
}

export type GetAllTransactionsExportProps = Omit<
  GetAllTransactionsProps,
  "page" | "pageSize"
>;

export type DrawdownFieldsRequestDTO = {
  invoiceFiles: string[];
  vendorId: string;
  invoices: Invoice[];
  currency: string;
  requestedAmountInAED?: number;
};

export type BulkDrawdownParseRequestDTO = {
  vendorId: string;
  currency: string;
  templateFileId: string;
};

export type BulkDrawdownParseResponseDTO = {
  status: "errors" | "success";
  totalAmount?: number;
  numberOfInvoices?: number;
  errors?: Array<{ row?: number; message: string }>;
  successes?: Array<{
    invoiceNo: string;
    invoiceAmount: number;
    requestedAmount: number;
    issuedDate: string;
    dueDate: string;
  }>;
};

export type EligibleCurrenciesResponseDTO = {
  currencies: string[];
  baseCurrency: string;
};

export type CalculateCurrencyConversionRequestDTO = {
  loanApplicationId: string;
  sourceCurrency: string;
  targetCurrency: string;
  amount: number;
};

export type CalculateCurrencyConversionResponseDTO = {
  sourceCurrency: string;
  targetCurrency: string;
  originalAmount: number;
  convertedAmount: number;
};

export enum LoanApplicationCaseStatus {
  IN_REVIEW_RM_INITIAL = "IN_REVIEW_RM_INITIAL",
  IN_REVIEW_LOAN_OPS = "IN_REVIEW_LOAN_OPS",
  IN_REVIEW_MLRO = "IN_REVIEW_MLRO",
  IN_REVIEW_UW = "IN_REVIEW_UW",
  IN_REVIEW_SUW = "IN_REVIEW_SUW",
  IN_REVIEW_CRO = "IN_REVIEW_CRO",
  IN_REVIEW_RM = "IN_REVIEW_RM",
  IN_REVIEW_RM_RECALL = "IN_REVIEW_RM_RECALL",
  ADDITIONAL_INFO_REQUIRED = "ADDITIONAL_INFO_REQUIRED",
  APPROVED_SUW = "APPROVED_SUW",
  APPROVED_CRO = "APPROVED_CRO",
  GENERATE_OFFER = "GENERATE_OFFER",
  OFFER_SENT = "OFFER_SENT",
  OFFER_ACCEPTED = "OFFER_ACCEPTED",
  OFFER_REJECTED = "OFFER_REJECTED",
  CONTRACT_SENT = "CONTRACT_SENT",
  CONTRACT_SENT_OFFLINE = "CONTRACT_SENT_OFFLINE",
  CONTRACT_PENDING_SIGNATURE = "CONTRACT_PENDING_SIGNATURE",
  CONTRACT_SIGNED = "CONTRACT_SIGNED",
  SEND_TO_SERVICING = "SEND_TO_SERVICING",
  DECLINED_MLRO = "DECLINED_MLRO",
  DECLINED_SUW = "DECLINED_SUW",
  DECLINED_CRO = "DECLINED_CRO",
  DECLINED_SME = "DECLINED_SME",
  DECLINED_LOAN_OPS = "DECLINED_LOAN_OPS",
  DECLINED_RM = "DECLINED_RM",
  WAITING_FOR_DISBURSEMENT = "WAITING_FOR_DISBURSEMENT",
  ACTIVE = "ACTIVE",
  PAUSED = "PAUSED",
  PAST_DUE = "PAST_DUE",
  PAST_MATURITY = "PAST_MATURITY",
  OVERPAID = "OVERPAID",
  EARLY_CLOSURE = "EARLY_CLOSURE",
  FORCED_CLOSURE = "FORCED_CLOSURE",
  CLOSED_OBLIGATIONS_MET = "CLOSED_OBLIGATIONS_MET",
  CLOSED_WRITTEN_OFF = "CLOSED_WRITTEN_OFF",
  CLOSED_RESCHEDULED = "CLOSED_RESCHEDULED",
  RESTRUCTURED = "RESTRUCTURED",
  EXPIRED = "EXPIRED",
  WITHDRAWN = "WITHDRAWN",
  ON_HOLD = "ON_HOLD",
}

export const LoanApplicationCaseStatusLabels: StatusLabels<
  typeof LoanApplicationCaseStatus
> = {
  IN_REVIEW_RM_INITIAL: {
    label: "In review",
    value: LoanApplicationCaseStatus.IN_REVIEW_RM_INITIAL,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  IN_REVIEW_LOAN_OPS: {
    label: "In review",
    value: LoanApplicationCaseStatus.IN_REVIEW_LOAN_OPS,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  IN_REVIEW_MLRO: {
    label: "In review",
    value: LoanApplicationCaseStatus.IN_REVIEW_MLRO,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  IN_REVIEW_UW: {
    label: "In review",
    value: LoanApplicationCaseStatus.IN_REVIEW_UW,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  IN_REVIEW_SUW: {
    label: "In review",
    value: LoanApplicationCaseStatus.IN_REVIEW_SUW,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  IN_REVIEW_CRO: {
    label: "In review",
    value: LoanApplicationCaseStatus.IN_REVIEW_CRO,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  IN_REVIEW_RM: {
    label: "In review",
    value: LoanApplicationCaseStatus.IN_REVIEW_RM,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  IN_REVIEW_RM_RECALL: {
    label: "In review",
    value: LoanApplicationCaseStatus.IN_REVIEW_RM_RECALL,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  ADDITIONAL_INFO_REQUIRED: {
    label: "Action required",
    value: LoanApplicationCaseStatus.ADDITIONAL_INFO_REQUIRED,
    displayClasses: "cx-bg-red-100 cx-text-red-600",
  },
  APPROVED_SUW: {
    label: "In review",
    value: LoanApplicationCaseStatus.APPROVED_SUW,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  APPROVED_CRO: {
    label: "In review",
    value: LoanApplicationCaseStatus.APPROVED_CRO,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  GENERATE_OFFER: {
    label: "In review",
    value: LoanApplicationCaseStatus.GENERATE_OFFER,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  OFFER_SENT: {
    label: "Offer sent",
    value: LoanApplicationCaseStatus.OFFER_SENT,
    displayClasses: "cx-text-[#0064FF] cx-bg-[#EDF5FF]",
  },
  OFFER_ACCEPTED: {
    label: "Offer accepted",
    value: LoanApplicationCaseStatus.OFFER_ACCEPTED,
    displayClasses: "cx-text-[#108723] cx-bg-[#E8FFED]",
  },
  OFFER_REJECTED: {
    label: "Offer rejected",
    value: LoanApplicationCaseStatus.OFFER_REJECTED,
    displayClasses: "cx-bg-red-100 cx-text-red-700",
  },
  CONTRACT_SENT: {
    label: "Contract pending signature",
    value: LoanApplicationCaseStatus.CONTRACT_SENT,
    displayClasses: "cx-text-[#0064FF] cx-bg-[#EDF5FF]",
  },
  CONTRACT_SENT_OFFLINE: {
    label: "Contract pending signature",
    value: LoanApplicationCaseStatus.CONTRACT_SENT_OFFLINE,
    displayClasses: "cx-text-[#0064FF] cx-bg-[#EDF5FF]",
  },
  CONTRACT_PENDING_SIGNATURE: {
    label: "Contract pending signature",
    value: LoanApplicationCaseStatus.CONTRACT_PENDING_SIGNATURE,
    displayClasses: "cx-text-[#EB6200] cx-bg-[#FEF6EE]",
  },
  CONTRACT_SIGNED: {
    label: "Contract signed",
    value: LoanApplicationCaseStatus.CONTRACT_SIGNED,
    displayClasses: "cx-text-[#108723] cx-bg-[#E8FFED]",
  },
  SEND_TO_SERVICING: {
    label: "Awaiting Disbursement",
    value: LoanApplicationCaseStatus.SEND_TO_SERVICING,
    displayClasses: "cx-text-[#0064FF] cx-bg-[#EDF5FF]",
  },
  DECLINED_MLRO: {
    label: "Rejected",
    value: LoanApplicationCaseStatus.DECLINED_MLRO,
    displayClasses: "cx-bg-red-100 cx-text-red-700",
  },
  DECLINED_LOAN_OPS: {
    label: "Rejected",
    value: LoanApplicationCaseStatus.DECLINED_LOAN_OPS,
    displayClasses: "cx-bg-red-100 cx-text-red-700",
  },
  DECLINED_RM: {
    label: "Rejected",
    value: LoanApplicationCaseStatus.DECLINED_RM,
    displayClasses: "cx-bg-red-100 cx-text-red-700",
  },
  DECLINED_SUW: {
    label: "Rejected",
    value: LoanApplicationCaseStatus.DECLINED_SUW,
    displayClasses: "cx-bg-red-100 cx-text-red-700",
  },
  DECLINED_CRO: {
    label: "Rejected",
    value: LoanApplicationCaseStatus.DECLINED_CRO,
    displayClasses: "cx-bg-red-100 cx-text-red-700",
  },
  DECLINED_SME: {
    label: "Offer rejected",
    value: LoanApplicationCaseStatus.DECLINED_SME,
    displayClasses: "cx-bg-red-100 cx-text-red-700",
  },
  WAITING_FOR_DISBURSEMENT: {
    label: "Awaiting Disbursement",
    value: LoanApplicationCaseStatus.WAITING_FOR_DISBURSEMENT,
    displayClasses: "cx-text-[#0064FF] cx-bg-[#EDF5FF]",
  },
  ACTIVE: {
    label: "Active",
    value: LoanApplicationCaseStatus.ACTIVE,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  RESTRUCTURED: {
    label: "Active",
    value: LoanApplicationCaseStatus.RESTRUCTURED,
    displayClasses: "cx-text-[#088A86] cx-bg-[#D9FBFB]",
  },
  WITHDRAWN: {
    label: "Withdrawn",
    value: LoanApplicationCaseStatus.WITHDRAWN,
    displayClasses: "cx-text-[#8E6A00] cx-bg-[#FCF4D6]",
  },
  ON_HOLD: {
    label: "On hold",
    value: LoanApplicationCaseStatus.ON_HOLD,
    displayClasses: "cx-text-[#EB6200] cx-bg-[#FEF6EE]",
  },
  EARLY_CLOSURE: {
    label: "Closed",
    value: LoanApplicationCaseStatus.EARLY_CLOSURE,
    displayClasses: "cx-text-[#666666] cx-bg-[#EEEEEE]",
  },
  FORCED_CLOSURE: {
    label: "Closed",
    value: LoanApplicationCaseStatus.FORCED_CLOSURE,
    displayClasses: "cx-text-[#666666] cx-bg-[#EEEEEE]",
  },
  PAUSED: {
    label: "Inactive",
    value: LoanApplicationCaseStatus.PAUSED,
    displayClasses: "cx-text-text-secondary cx-bg-background-secondary",
  },
  PAST_DUE: {
    label: "Overdue",
    value: LoanApplicationCaseStatus.PAST_DUE,
    displayClasses: "cx-text-[#A2191F] cx-bg-[#FFF1F1]",
  },
  PAST_MATURITY: {
    label: "Late Fee Applied",
    value: LoanApplicationCaseStatus.PAST_MATURITY,
    displayClasses: "cx-text-[#A2191F] cx-bg-[#FFF1F1]",
  },
  OVERPAID: {
    label: "Active",
    value: LoanApplicationCaseStatus.OVERPAID,
    displayClasses: "cx-text-text-success cx-bg-success-lighter",
  },
  EXPIRED: {
    label: "Expired",
    value: LoanApplicationCaseStatus.EXPIRED,
    displayClasses: "cx-text-text-secondary cx-bg-background-secondary",
  },
  CLOSED_OBLIGATIONS_MET: {
    label: "Closed",
    value: LoanApplicationCaseStatus.CLOSED_OBLIGATIONS_MET,
    displayClasses: "cx-text-text-secondary cx-bg-background-secondary",
  },
  CLOSED_WRITTEN_OFF: {
    label: "Closed",
    value: LoanApplicationCaseStatus.CLOSED_WRITTEN_OFF,
    displayClasses: "cx-text-text-secondary cx-bg-background-secondary",
  },
  CLOSED_RESCHEDULED: {
    label: "Closed",
    value: LoanApplicationCaseStatus.CLOSED_RESCHEDULED,
    displayClasses: "cx-text-text-secondary cx-bg-background-secondary",
  },
};

export const LoanApplicationCaseStatusLabelsForCreditLine: StatusLabels<
  typeof LoanApplicationCaseStatus
> = {
  ...LoanApplicationCaseStatusLabels,
  SEND_TO_SERVICING: {
    ...LoanApplicationCaseStatusLabels.SEND_TO_SERVICING,
    label: "Awaiting Activation",
  },
  WAITING_FOR_DISBURSEMENT: {
    ...LoanApplicationCaseStatusLabels.WAITING_FOR_DISBURSEMENT,
    label: "Awaiting Activation",
  },
};

export const LoanApplicationCaseStatusDashboardMappingToText: Record<
  LoanApplicationCaseStatus,
  string
> = {
  [LoanApplicationCaseStatus.IN_REVIEW_RM_INITIAL]: "Requested Facility",
  [LoanApplicationCaseStatus.IN_REVIEW_LOAN_OPS]: "Requested Facility",
  [LoanApplicationCaseStatus.IN_REVIEW_MLRO]: "Requested Facility",
  [LoanApplicationCaseStatus.IN_REVIEW_UW]: "Requested Facility",
  [LoanApplicationCaseStatus.IN_REVIEW_SUW]: "Requested Facility",
  [LoanApplicationCaseStatus.IN_REVIEW_CRO]: "Requested Facility",
  [LoanApplicationCaseStatus.IN_REVIEW_RM]: "Requested Facility",
  [LoanApplicationCaseStatus.IN_REVIEW_RM_RECALL]: "Requested Facility",
  [LoanApplicationCaseStatus.ADDITIONAL_INFO_REQUIRED]: "Requested Facility",
  [LoanApplicationCaseStatus.APPROVED_SUW]: "Requested Facility",
  [LoanApplicationCaseStatus.APPROVED_CRO]: "Requested Facility",
  [LoanApplicationCaseStatus.GENERATE_OFFER]: "Requested Facility",
  [LoanApplicationCaseStatus.OFFER_SENT]: "Proposed Facility",
  [LoanApplicationCaseStatus.OFFER_ACCEPTED]: "Approved Facility",
  [LoanApplicationCaseStatus.OFFER_REJECTED]: "Proposed Facility",
  [LoanApplicationCaseStatus.CONTRACT_SENT]: "Approved Facility",
  [LoanApplicationCaseStatus.CONTRACT_SENT_OFFLINE]: "Approved Facility",
  [LoanApplicationCaseStatus.CONTRACT_PENDING_SIGNATURE]: "Approved Facility",
  [LoanApplicationCaseStatus.CONTRACT_SIGNED]: "Approved Facility",
  [LoanApplicationCaseStatus.SEND_TO_SERVICING]: "Approved Facility",
  [LoanApplicationCaseStatus.DECLINED_MLRO]: "Requested Facility",
  [LoanApplicationCaseStatus.DECLINED_SUW]: "Requested Facility",
  [LoanApplicationCaseStatus.DECLINED_CRO]: "Requested Facility",
  [LoanApplicationCaseStatus.DECLINED_SME]: "Proposed Facility",
  [LoanApplicationCaseStatus.WAITING_FOR_DISBURSEMENT]: "Approved Facility",
  [LoanApplicationCaseStatus.ACTIVE]: "Disbursed Facility",
  [LoanApplicationCaseStatus.RESTRUCTURED]: "Disbursed Facility",
  [LoanApplicationCaseStatus.WITHDRAWN]: "Requested Facility",
  [LoanApplicationCaseStatus.ON_HOLD]: "Requested Facility",
  [LoanApplicationCaseStatus.DECLINED_LOAN_OPS]: "Requested Facility",
  [LoanApplicationCaseStatus.DECLINED_RM]: "Requested Facility",
  [LoanApplicationCaseStatus.EARLY_CLOSURE]: "Disbursed Facility",
  [LoanApplicationCaseStatus.FORCED_CLOSURE]: "Disbursed Facility",
  [LoanApplicationCaseStatus.PAUSED]: "Disbursed Facility",
  [LoanApplicationCaseStatus.PAST_DUE]: "Disbursed Facility",
  [LoanApplicationCaseStatus.PAST_MATURITY]: "Disbursed Facility",
  [LoanApplicationCaseStatus.OVERPAID]: "Disbursed Facility",
  [LoanApplicationCaseStatus.EXPIRED]: "Disbursed Facility",
  [LoanApplicationCaseStatus.CLOSED_OBLIGATIONS_MET]: "Disbursed Facility",
  [LoanApplicationCaseStatus.CLOSED_WRITTEN_OFF]: "Disbursed Facility",
  [LoanApplicationCaseStatus.CLOSED_RESCHEDULED]: "Disbursed Facility",
};

export const LoanApplicationCaseStatusForCreditLineDashboardMappingToText: Record<
  LoanApplicationCaseStatus,
  string
> = {
  ...LoanApplicationCaseStatusDashboardMappingToText,
  [LoanApplicationCaseStatus.ACTIVE]: "Approved Facility",
  [LoanApplicationCaseStatus.EARLY_CLOSURE]: "Disbursed Facility",
  [LoanApplicationCaseStatus.FORCED_CLOSURE]: "Disbursed Facility",
  [LoanApplicationCaseStatus.PAUSED]: "Approved Facility",
  [LoanApplicationCaseStatus.PAST_DUE]: "Approved Facility",
  [LoanApplicationCaseStatus.PAST_MATURITY]: "Approved Facility",
  [LoanApplicationCaseStatus.OVERPAID]: "Approved Facility",
  [LoanApplicationCaseStatus.EXPIRED]: "Approved Facility",
  [LoanApplicationCaseStatus.CLOSED_OBLIGATIONS_MET]: "Approved Facility",
  [LoanApplicationCaseStatus.CLOSED_WRITTEN_OFF]: "Approved Facility",
  [LoanApplicationCaseStatus.CLOSED_RESCHEDULED]: "Approved Facility",
};

export enum LOSPlatform {
  BIZ2X = "BIZ2X",
  CREDIBLEX = "CREDIBLEX",
}

export enum DrawdownStatus {
  SUBMITTED = "SUBMITTED",
  PENDING_POA_REVIEW = "PENDING_POA_REVIEW",
  POA_REJECTED = "POA_REJECTED",

  IN_REVIEW_OPS = "IN_REVIEW_OPS",
  ADDITIONAL_INFO_REQUIRED = "ADDITIONAL_INFO_REQUIRED",
  IN_REVIEW_SENIOR_OPS = "IN_REVIEW_SENIOR_OPS",

  SEND_TO_LMS = "SEND_TO_LMS",
  APPROVED = "APPROVED",
  WAITING_DISBURSAL = "WAITING_DISBURSAL",

  ACTIVE = "ACTIVE",
  PAST_DUE = "PAST_DUE",
  PAST_MATURITY = "PAST_MATURITY",
  CLOSED_OBLIGATIONS_MET = "CLOSED_OBLIGATIONS_MET",
  CLOSED_WRITTEN_OFF = "CLOSED_WRITTEN_OFF",
  CLOSED_RESCHEDULED = "CLOSED_RESCHEDULED",
  OVERPAID = "OVERPAID",
  FORCED_CLOSURE = "FORCED_CLOSURE",
  EARLY_CLOSURE = "EARLY_CLOSURE",

  ON_HOLD = "ON_HOLD",
  DISAPPROVED = "DISAPPROVED",
  DECLINED_OPS = "DECLINED_OPS",
}

export const DrawdownStatusDisplayMap: Record<DrawdownStatus, string> = {
  [DrawdownStatus.SUBMITTED]: "Submitted",
  [DrawdownStatus.PENDING_POA_REVIEW]: "Pending Approval",
  [DrawdownStatus.IN_REVIEW_OPS]: "In Review",
  [DrawdownStatus.IN_REVIEW_SENIOR_OPS]: "In Review",
  [DrawdownStatus.ON_HOLD]: "In Review",
  [DrawdownStatus.ADDITIONAL_INFO_REQUIRED]: "Action Required",
  [DrawdownStatus.PAST_DUE]: "Overdue",
  [DrawdownStatus.ACTIVE]: "Active",
  [DrawdownStatus.OVERPAID]: "Active",
  [DrawdownStatus.SEND_TO_LMS]: "Awaiting Disbursement",
  [DrawdownStatus.APPROVED]: "Awaiting Disbursement",
  [DrawdownStatus.WAITING_DISBURSAL]: "Awaiting Disbursement",
  [DrawdownStatus.PAST_MATURITY]: "Late fee applied",
  [DrawdownStatus.FORCED_CLOSURE]: "Closed",
  [DrawdownStatus.EARLY_CLOSURE]: "Closed",
  [DrawdownStatus.CLOSED_WRITTEN_OFF]: "Closed",
  [DrawdownStatus.CLOSED_OBLIGATIONS_MET]: "Closed",
  [DrawdownStatus.CLOSED_RESCHEDULED]: "Closed",
  [DrawdownStatus.POA_REJECTED]: "Rejected by POA",
  [DrawdownStatus.DECLINED_OPS]: "Rejected",
  [DrawdownStatus.DISAPPROVED]: "Rejected",
};

export const DrawdownStatusColorMap: Record<DrawdownStatus, string> = {
  [DrawdownStatus.SUBMITTED]: "cx-bg-[#FFF3E0] cx-text-[#F57C00]",
  [DrawdownStatus.PENDING_POA_REVIEW]: "cx-bg-[#E8F5E9] cx-text-[#388E3C]",
  [DrawdownStatus.IN_REVIEW_OPS]: "cx-bg-[#E8F5E9] cx-text-[#388E3C]",
  [DrawdownStatus.ADDITIONAL_INFO_REQUIRED]:
    "cx-bg-[#FFF1F1] cx-text-[#DA1E28]",
  [DrawdownStatus.IN_REVIEW_SENIOR_OPS]: "cx-bg-[#E8F5E9] cx-text-[#388E3C]",
  [DrawdownStatus.WAITING_DISBURSAL]: "cx-bg-[#E8FFED] cx-text-[#108723]",
  [DrawdownStatus.PAST_DUE]: "cx-bg-[#FFF1F1] cx-text-[#EB6200]",
  [DrawdownStatus.FORCED_CLOSURE]: "cx-bg-[#EEEEEE] cx-text-[#666666]",
  [DrawdownStatus.EARLY_CLOSURE]: "cx-bg-[#EEEEEE] cx-text-[#666666]",
  [DrawdownStatus.DECLINED_OPS]: "cx-text-[#FF4500] cx-bg-[#FF45001A]",
  [DrawdownStatus.ACTIVE]: "cx-bg-[#E8F5E9] cx-text-[#388E3C]",
  [DrawdownStatus.OVERPAID]: "cx-bg-[#E8F5E9] cx-text-[#388E3C]",
  [DrawdownStatus.CLOSED_WRITTEN_OFF]: "cx-bg-[#EEEEEE] cx-text-[#666666]",
  [DrawdownStatus.CLOSED_OBLIGATIONS_MET]: "cx-bg-[#EEEEEE] cx-text-[#666666]",
  [DrawdownStatus.POA_REJECTED]: "cx-text-[#FF4500] cx-bg-[#FF45001A]",
  [DrawdownStatus.SEND_TO_LMS]: "cx-bg-[#E8FFED] cx-text-[#108723]",
  [DrawdownStatus.APPROVED]: "cx-bg-[#E8FFED] cx-text-[#108723]",
  [DrawdownStatus.PAST_MATURITY]: "cx-bg-[#FFF1F1] cx-text-[#EB6200]",
  [DrawdownStatus.CLOSED_RESCHEDULED]: "cx-bg-[#EEEEEE] cx-text-[#666666]",
  [DrawdownStatus.ON_HOLD]: "cx-bg-[#E8F5E9] cx-text-[#388E3C]",
  [DrawdownStatus.DISAPPROVED]: "cx-text-[#FF4500] cx-bg-[#FF45001A]",
};

// New reverse map type
export type StatusLabelReverseMap = {
  [label: string]: {
    statuses: LoanApplicationStatus[];
    caseStatuses: LoanApplicationCaseStatus[];
    color: string;
  };
};

const extractColorFromClass = (displayClasses: string): string => {
  const hexMatch = displayClasses.match(/cx-(?:text|bg)-\[#([A-Fa-f0-9]{6})\]/);
  if (hexMatch) {
    return `#${hexMatch[1]}`;
  }

  if (
    displayClasses.includes("cx-text-red") ||
    displayClasses.includes("cx-bg-red")
  ) {
    return "#ef4444";
  }
  if (
    displayClasses.includes("cx-text-success") ||
    displayClasses.includes("cx-bg-success")
  ) {
    return "#22c55e";
  }
  if (
    displayClasses.includes("cx-text-warning") ||
    displayClasses.includes("cx-bg-warning")
  ) {
    return "#f59e0b";
  }
  return "#6b7280";
};

export const StatusLabelReverseMapping: StatusLabelReverseMap = (() => {
  const map: StatusLabelReverseMap = {};

  Object.entries(LoanApplicationStatusLabels).forEach(([key, item]) => {
    const label = item.label;
    const color = extractColorFromClass(item.displayClasses);

    if (!map[label]) {
      map[label] = { statuses: [], caseStatuses: [], color };
    }
    map[label].statuses.push(key as LoanApplicationStatus);
    if (!map[label].color) {
      map[label].color = color;
    }
  });

  Object.entries(LoanApplicationCaseStatusLabels).forEach(([key, item]) => {
    const label = item.label;
    const color = extractColorFromClass(item.displayClasses);

    if (!map[label]) {
      map[label] = { statuses: [], caseStatuses: [], color };
    }
    map[label].caseStatuses.push(key as LoanApplicationCaseStatus);
    if (!map[label].color) {
      map[label].color = color;
    }
  });

  return map;
})();
